// src/components/TenurePopupDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Pagination,
  Tooltip,
  AlertTitle,
  Snackbar,
  SnackbarContent,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  Warning as WarningIcon,
  Group as GroupIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

const TenurePopupDialog = ({ open, onClose, minMonths = 3, onViewStaff }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [staffData, setStaffData] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [markingInProgress, setMarkingInProgress] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (open) {
      fetchTenureStaff();
    }
  }, [open, minMonths]);

  const fetchTenureStaff = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const url = `${baseUrl}/get_appraisal_staff.php?min_months=${minMonths}&include_inactive=false`;
      
      console.log('Fetching from URL:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('API Response:', result);
      
      if (result.success) {
        setStaffData(result.data || []);
        setStats(result.stats);
      } else {
        setError(result.message || 'Failed to fetch staff data');
      }
    } catch (err) {
      console.error('Error fetching tenure staff:', err);
      if (err.message.includes('Failed to fetch') || err.name === 'TypeError') {
        setError('Network error: Unable to connect to the server. Please check your internet connection and try again.');
      } else {
        setError(err.message || 'Network error occurred. Please refresh the page.');
      }
    } finally {
      setLoading(false);
    }
  };

  const markForAppraisal = async (staff) => {
    try {
      setMarkingInProgress(staff.id);
      
      const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
      const url = `${baseUrl}/get_appraisal_staff.php`;
      
      const response = await fetch(url, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          staff_id: staff.id,
          phone: staff.phone,
          name: staff.full_name,
        }),
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Remove the staff from the list
        setStaffData(prevData => prevData.filter(s => s.id !== staff.id));
        
        // Update stats
        setStats(prevStats => ({
          ...prevStats,
          total: (prevStats?.total || 0) - 1,
        }));
        
        setSnackbar({
          open: true,
          message: `${staff.full_name} has been marked for appraisal successfully!`,
          severity: 'success',
        });
      } else {
        if (result.already_exists) {
          // Staff already has an appraisal, remove them from list
          setStaffData(prevData => prevData.filter(s => s.id !== staff.id));
          setSnackbar({
            open: true,
            message: `${staff.full_name} already has a pending appraisal. Removed from list.`,
            severity: 'info',
          });
        } else {
          setSnackbar({
            open: true,
            message: result.message || 'Failed to mark staff for appraisal',
            severity: 'error',
          });
        }
      }
    } catch (err) {
      console.error('Error marking for appraisal:', err);
      setSnackbar({
        open: true,
        message: 'Network error. Please try again.',
        severity: 'error',
      });
    } finally {
      setMarkingInProgress(null);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Filter staff based on search and department
  const filteredStaff = staffData.filter(staff => {
    const matchesSearch = searchTerm === '' || 
      staff.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      staff.surname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      staff.other_names?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      staff.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      staff.phone?.includes(searchTerm);
    
    const matchesDepartment = departmentFilter === 'all' || staff.department === departmentFilter;
    
    return matchesSearch && matchesDepartment;
  });

  // Pagination
  const totalPages = Math.ceil(filteredStaff.length / rowsPerPage);
  const paginatedStaff = filteredStaff.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  // Get unique departments for filter
  const uniqueDepartments = ['all', ...new Set(staffData.map(s => s.department).filter(Boolean))];

  // Tenure category colors
  const getTenureColor = (category) => {
    switch (category) {
      case '3-5 months': return '#4caf50';
      case '6-11 months': return '#2196f3';
      case '1-2 years': return '#ff9800';
      case '2+ years': return '#9c27b0';
      default: return '#757575';
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Full Name', 'Department', 'Phone', 'Email', 'Hire Date', 'Months Employed', 'Years Employed', 'Status', 'Tenure Category'];
    const csvData = filteredStaff.map(staff => [
      staff.full_name,
      staff.department,
      staff.phone,
      staff.email,
      staff.hire_date_formatted,
      staff.months_employed,
      staff.years_employed,
      staff.status,
      staff.tenure_category
    ]);
    
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `staff_for_appraisal_${minMonths}_months_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleViewStaff = (staff) => {
    if (onViewStaff) {
      onViewStaff(staff);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            minHeight: '60vh',
            maxHeight: '85vh',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: '#1976d2',
          color: 'white',
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssignmentIcon />
            <Typography variant="h6" fontWeight="bold">
              Staff Ready for Appraisal ({minMonths}+ Months Tenure)
            </Typography>
            <Chip 
              label={`${stats?.total || 0} staff members`} 
              size="small" 
              sx={{ ml: 1, bgcolor: 'white', color: '#1976d2', fontWeight: 'bold' }} 
            />
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* Info Alert */}
         

         
          {/* Department Breakdown */}
          {stats?.department_breakdown && stats.department_breakdown.length > 0 && (
            <Paper sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: '#fafafa' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon fontSize="small" /> Department Breakdown
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {stats.department_breakdown.map((dept, idx) => (
                  <Chip
                    key={idx}
                    label={`${dept.department}: ${dept.count} staff (Avg ${dept.avg_tenure_months} mos)`}
                    variant="outlined"
                    onClick={() => setDepartmentFilter(dept.department)}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#e3f2fd' } }}
                  />
                ))}
              </Box>
            </Paper>
          )}

          {/* Search and Filter Bar */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search by name, department, or phone..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              sx={{ flex: 2, minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Department</InputLabel>
              <Select
                value={departmentFilter}
                onChange={(e) => {
                  setDepartmentFilter(e.target.value);
                  setPage(1);
                }}
                label="Department"
              >
                <MenuItem value="all">All Departments</MenuItem>
                {uniqueDepartments.filter(d => d !== 'all').map((dept, idx) => (
                  <MenuItem key={idx} value={dept}>{dept}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Rows</InputLabel>
              <Select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(e.target.value);
                  setPage(1);
                }}
                label="Rows"
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportToCSV}
              disabled={filteredStaff.length === 0}
            >
              Export CSV
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchTenureStaff}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>

          {/* Loading State */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Error State */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={fetchTenureStaff}>
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          )}

          {/* Staff Table */}
          {!loading && !error && staffData.length === 0 && (
            <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>
              All eligible staff have been marked for appraisal! No pending staff to show.
            </Alert>
          )}

          {!loading && !error && staffData.length > 0 && (
            <>
              <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table size="medium">
                  <TableHead sx={{ bgcolor: '#f5f5f5' }}>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Full Name</TableCell>
                      <TableCell>Department</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Hire Date</TableCell>
                      <TableCell align="center">Tenure</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedStaff.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                          <Typography variant="body1" color="textSecondary">
                            No staff found matching your criteria
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedStaff.map((staff, index) => {
                        const globalIndex = (page - 1) * rowsPerPage + index + 1;
                        return (
                          <TableRow key={staff.id} hover>
                            <TableCell>{globalIndex}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Tooltip title={`${staff.years_employed} years, ${staff.months_employed % 12} months total`}>
                                  <Chip 
                                    size="small" 
                                    label={`${staff.months_employed}m`}
                                    sx={{ 
                                      bgcolor: getTenureColor(staff.tenure_category),
                                      color: 'white',
                                      fontWeight: 'bold',
                                      minWidth: 50
                                    }}
                                  />
                                </Tooltip>
                                <Typography variant="body2" fontWeight="medium">
                                  {staff.full_name}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={staff.department || 'Not Assigned'} 
                                size="small" 
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{staff.phone || 'N/A'}</Typography>
                              <Typography variant="caption" color="textSecondary">
                                {staff.email || 'No email'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CalendarIcon sx={{ fontSize: 14, color: 'textSecondary' }} />
                                <Typography variant="body2">
                                  {staff.hire_date_formatted}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title={`${staff.days_employed} days since hire`}>
                                <Chip
                                  label={staff.tenure_category}
                                  size="small"
                                  sx={{
                                    bgcolor: getTenureColor(staff.tenure_category) + '20',
                                    color: getTenureColor(staff.tenure_category),
                                    fontWeight: 'medium'
                                  }}
                                />
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                variant="contained"
                                size="small"
                                color="primary"
                                startIcon={markingInProgress === staff.id ? <CircularProgress size={16} color="inherit" /> : <AssignmentIcon />}
                                onClick={() => markForAppraisal(staff)}
                                disabled={markingInProgress === staff.id}
                                sx={{
                                  textTransform: 'none',
                                  borderRadius: 2,
                                }}
                              >
                                {markingInProgress === staff.id ? 'Marking...' : 'Mark for Appraisal'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}

              {/* Summary */}
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
                Showing {paginatedStaff.length} of {filteredStaff.length} staff members
                {departmentFilter !== 'all' && ` in ${departmentFilter} department`}
                {searchTerm && ` matching "${searchTerm}"`}
              </Typography>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button onClick={onClose} variant="outlined">
            Close
          </Button>
          <Button 
            onClick={() => {
              setSearchTerm('');
              setDepartmentFilter('all');
              setPage(1);
            }} 
            variant="text"
          >
            Reset Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <SnackbarContent
          sx={{
            bgcolor: snackbar.severity === 'success' ? '#4caf50' : snackbar.severity === 'error' ? '#d32f2f' : '#2196f3',
            color: 'white',
          }}
          message={snackbar.message}
          action={
            <IconButton size="small" onClick={handleCloseSnackbar} sx={{ color: 'white' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        />
      </Snackbar>
    </>
  );
};

export default TenurePopupDialog;